import { KeyIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import type { AccountSecurity } from '@/api'
import { Skeleton } from '@/components/Skeleton'
import { useAccountAppContext } from '@/app/AccountAppContext'

import { PasswordChangeDialog } from './PasswordChangeDialog'
import { usePasswordChange } from './usePasswordChange'

type PasswordSectionProps = {
    password?: AccountSecurity['password']
}

const PasswordCardSkeleton = () => {
    return (
        <section className="border-account-divider bg-account-surface overflow-hidden rounded-xl border shadow-sm">
            <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="space-y-2 pt-1">
                        <Skeleton className="h-5 w-28" />
                        <Skeleton className="h-4 w-64 max-w-full" />
                    </div>
                </div>

                <Skeleton className="h-10 w-40" />
            </div>
        </section>
    )
}

export const PasswordSection = ({ password }: PasswordSectionProps) => {
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [checkingAuthentication, setCheckingAuthentication] = useState(false)
    const openingRef = useRef(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const { ensureFreshAuthentication } = useAccountAppContext()

    const returnUrl = new URL(window.location.href)
    returnUrl.searchParams.set('action', 'change-password')
    const returnTo = returnUrl.toString()

    const changePassword = usePasswordChange(returnTo)

    const handleOpen = useCallback(async () => {
        if (openingRef.current) {
            return
        }

        openingRef.current = true
        setCheckingAuthentication(true)

        try {
            if (await ensureFreshAuthentication(returnTo)) {
                setIsOpen(true)
            }
        } finally {
            openingRef.current = false
            setCheckingAuthentication(false)
        }
    }, [ensureFreshAuthentication, returnTo])

    useEffect(() => {
        if (!password?.enabled || searchParams.get('action') !== 'change-password') {
            return
        }

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('action')
        setSearchParams(nextParams, { replace: true })
        void handleOpen()
    }, [handleOpen, password?.enabled, searchParams, setSearchParams])

    const handleSubmit = async (newPassword: string) => {
        await changePassword.mutateAsync({ newPassword })
        setIsOpen(false)
    }

    if (!password) {
        return <PasswordCardSkeleton />
    }

    return (
        <section className="border-account-divider bg-account-surface overflow-hidden rounded-xl border shadow-sm">
            {password.enabled ? (
                <>
                    <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-4">
                            <div className="border-account-divider bg-account-muted text-account-secondary mt-1 rounded-full border p-2.5">
                                <KeyIcon className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-account-strong font-bold">{t('password.itemTitle')}</h3>
                                <p className="text-account-secondary mt-1 text-sm leading-relaxed">
                                    {t('password.description')}
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    void handleOpen()
                                }}
                                disabled={checkingAuthentication}
                                className="border-account-border bg-account-surface text-account-primary hover:bg-account-muted whitespace-nowrap rounded-lg border px-5 py-2 font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {t('password.changeAction')}
                            </button>
                        </div>
                    </div>
                    <PasswordChangeDialog
                        open={isOpen}
                        minLength={password.minLength}
                        strength={password.strength}
                        onClose={() => setIsOpen(false)}
                        onSubmit={handleSubmit}
                    />
                </>
            ) : (
                <div className="p-6">
                    <p className="text-account-secondary max-w-2xl text-sm leading-6">{t('password.unavailable')}</p>
                </div>
            )}
        </section>
    )
}
